/**
 * MetaMask State Cleaner
 * Utility functions to help clear MetaMask's internal state when it gets stuck
 */

interface MetaMaskProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  isMetaMask?: boolean;
  selectedAddress?: string;
  chainId?: string;
}

/**
 * Get MetaMask provider
 */
function getMetaMaskProvider(): MetaMaskProvider | null {
  if (typeof window === "undefined") return null;

  const ethereum = (window as any).ethereum;
  if (!ethereum) return null;

  // If multiple providers exist, find MetaMask specifically
  if (ethereum.providers && Array.isArray(ethereum.providers)) {
    return (
      ethereum.providers.find((provider: any) => provider.isMetaMask) || null
    );
  }

  // Single provider case
  return ethereum.isMetaMask ? ethereum : null;
}

/**
 * Clear MetaMask's pending request state by making a safe request
 */
export async function clearMetaMaskPendingState(): Promise<boolean> {
  const provider = getMetaMaskProvider();
  if (!provider) {
    console.warn("MetaMask not found");
    return false;
  }

  try {
    // Make a safe request to clear any pending state
    await provider.request({ method: "eth_accounts" });
    console.log("MetaMask state cleared successfully");
    return true;
  } catch (error: any) {
    console.log(
      "MetaMask state clearing completed with expected error:",
      error.message
    );
    // Even if this fails, it often clears the pending state
    return true;
  }
}

/**
 * Force refresh MetaMask connection state
 */
export async function refreshMetaMaskConnection(): Promise<boolean> {
  const provider = getMetaMaskProvider();
  if (!provider) {
    console.warn("MetaMask not found");
    return false;
  }

  try {
    // Try to get current accounts
    const accounts = await provider.request({ method: "eth_accounts" });

    // If we have accounts, try to get the current chain
    if (accounts && accounts.length > 0) {
      await provider.request({ method: "eth_chainId" });
    }

    console.log("MetaMask connection refreshed successfully");
    return true;
  } catch (error: any) {
    console.warn("Failed to refresh MetaMask connection:", error.message);
    return false;
  }
}

/**
 * Check if MetaMask has pending requests
 */
export async function checkMetaMaskPendingRequests(): Promise<{
  hasPendingRequests: boolean;
  error?: any;
}> {
  const provider = getMetaMaskProvider();
  if (!provider) {
    return { hasPendingRequests: false };
  }

  try {
    // Try a quick request to see if there are pending requests
    await provider.request({ method: "eth_accounts" });
    return { hasPendingRequests: false };
  } catch (error: any) {
    if (error.code === -32002) {
      return { hasPendingRequests: true, error };
    }
    return { hasPendingRequests: false, error };
  }
}

/**
 * Comprehensive MetaMask state reset
 */
export async function resetMetaMaskState(): Promise<{
  success: boolean;
  steps: string[];
  errors: string[];
}> {
  const steps: string[] = [];
  const errors: string[] = [];

  steps.push("Starting MetaMask state reset...");

  // Step 1: Check if MetaMask is available
  const provider = getMetaMaskProvider();
  if (!provider) {
    errors.push("MetaMask not found or not installed");
    return { success: false, steps, errors };
  }
  steps.push("MetaMask provider found");

  // Step 2: Check for pending requests
  try {
    const pendingCheck = await checkMetaMaskPendingRequests();
    if (pendingCheck.hasPendingRequests) {
      steps.push("Detected pending requests in MetaMask");
    } else {
      steps.push("No pending requests detected");
    }
  } catch (error: any) {
    errors.push(`Failed to check pending requests: ${error.message}`);
  }

  // Step 3: Clear pending state
  try {
    await clearMetaMaskPendingState();
    steps.push("Attempted to clear pending state");
  } catch (error: any) {
    errors.push(`Failed to clear pending state: ${error.message}`);
  }

  // Step 4: Wait a moment for state to settle
  await new Promise((resolve) => setTimeout(resolve, 1000));
  steps.push("Waited for state to settle");

  // Step 5: Refresh connection
  try {
    const refreshed = await refreshMetaMaskConnection();
    if (refreshed) {
      steps.push("Successfully refreshed MetaMask connection");
    } else {
      steps.push("Failed to refresh MetaMask connection");
    }
  } catch (error: any) {
    errors.push(`Failed to refresh connection: ${error.message}`);
  }

  // Step 6: Final verification
  try {
    const finalCheck = await checkMetaMaskPendingRequests();
    if (!finalCheck.hasPendingRequests) {
      steps.push("Verification: No pending requests detected");
    } else {
      steps.push("Verification: Pending requests still detected");
      errors.push("State reset may not have been completely successful");
    }
  } catch (error: any) {
    errors.push(`Final verification failed: ${error.message}`);
  }

  const success = errors.length === 0;
  steps.push(
    `State reset ${
      success ? "completed successfully" : "completed with errors"
    }`
  );

  return { success, steps, errors };
}

/**
 * Get detailed MetaMask state information for debugging
 */
export async function getMetaMaskStateInfo(): Promise<{
  isInstalled: boolean;
  isConnected: boolean;
  accounts: string[];
  chainId: string | null;
  hasPendingRequests: boolean;
  error?: any;
}> {
  const provider = getMetaMaskProvider();

  if (!provider) {
    return {
      isInstalled: false,
      isConnected: false,
      accounts: [],
      chainId: null,
      hasPendingRequests: false,
    };
  }

  let accounts: string[] = [];
  let chainId: string | null = null;
  let hasPendingRequests = false;
  let error: any = null;

  try {
    // Try to get accounts
    accounts = (await provider.request({ method: "eth_accounts" })) || [];
  } catch (err: any) {
    error = err;
    if (err.code === -32002) {
      hasPendingRequests = true;
    }
  }

  try {
    // Try to get chain ID
    chainId = await provider.request({ method: "eth_chainId" });
  } catch (err: any) {
    if (!error) error = err;
  }

  return {
    isInstalled: true,
    isConnected: accounts.length > 0,
    accounts,
    chainId,
    hasPendingRequests,
    error,
  };
}
